import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Edit2, Trash2, Eye, EyeOff, MoreVertical } from 'lucide-react';
import type { UserModel } from '@visionar/shared';
import { useModelsStore } from '@/stores/models-store';
import { useUIStore } from '@/stores/ui-store';
import { useNotificationsStore } from '@/stores/notifications-store';
import ModelThumbnail3D from './ModelThumbnail3D';

interface UserModelCardProps {
  model: UserModel;
  onEdit?: (model: UserModel) => void;
  onClick?: (model: UserModel) => void;
}

export default function UserModelCard({ model, onEdit, onClick }: UserModelCardProps) {
  const { deleteUserModel, updateUserModel } = useModelsStore();
  const { showToast } = useUIStore();
  const { notifyModelDeleted } = useNotificationsStore();
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Calculate menu position when opening
  useEffect(() => {
    if (showMenu && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [showMenu]);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this model?')) return;

    setIsDeleting(true);
    setShowMenu(false);
    try {
      const modelName = model.name;
      await deleteUserModel(model.id);
      notifyModelDeleted(modelName);
      showToast('Model deleted', 'success');
    } catch (error) {
      console.error('Failed to delete model:', error);
      showToast('Failed to delete model', 'error');
      setIsDeleting(false);
    }
  };

  const handleTogglePublic = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateUserModel(model.id, { isPublic: !model.isPublic });
      setShowMenu(false);
    } catch (error) {
      console.error('Failed to update model:', error);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(model);
    setShowMenu(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(new Date(date));
  };

  const handleCardClick = () => {
    if (!showMenu && onClick) {
      onClick(model);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        whileTap={{ scale: 0.96 }}
        onClick={handleCardClick}
        className={`glass rounded-3xl p-3 cursor-pointer group relative ${
          isDeleting ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        {/* 3D Model Thumbnail */}
        <div className="relative h-36 rounded-2xl overflow-hidden mb-3">
          <ModelThumbnail3D modelUrl={model.modelUrl} className="w-full h-full rounded-2xl" />

          {/* Public/Private Badge */}
          <div className="absolute top-3 left-2 z-10">
            <div
              className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase flex items-center gap-1 backdrop-blur-sm ${
                model.isPublic
                  ? 'bg-green-500/80 text-white'
                  : 'bg-black/40 text-white/80'
              }`}
            >
              {model.isPublic ? (
                <>
                  <Eye size={10} />
                  Public
                </>
              ) : (
                <>
                  <EyeOff size={10} />
                  Private
                </>
              )}
            </div>
          </div>

          {/* File size badge */}
          <div className="absolute bottom-2 left-2 z-10">
            <div className="px-2 py-1 rounded-full text-[10px] font-medium bg-black/40 backdrop-blur-sm text-white/80">
              {formatFileSize(model.fileSize)}
            </div>
          </div>

          {/* Menu Button */}
          <div className="absolute top-3 right-2 z-10">
            <button
              ref={menuButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                showMenu
                  ? 'bg-white/20 scale-110'
                  : 'bg-black/40 backdrop-blur-sm hover:scale-110 hover:bg-black/60'
              }`}
            >
              <MoreVertical size={14} />
            </button>
          </div>
        </div>

        {/* Model Info */}
        <h4 className="font-semibold text-sm mb-0.5 truncate">{model.name}</h4>

        {/* Meta row */}
        <div className="flex items-center justify-between text-[11px] text-white/50">
          <span className="flex items-center gap-1">
            <Eye size={11} />
            {model.viewCount}
          </span>
          <span>{formatDate(model.createdAt)}</span>
        </div>
      </motion.div>

      {/* Portal for dropdown menu */}
      {createPortal(
        <AnimatePresence>
          {showMenu && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-[100]"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />
              {/* Menu */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -8 }}
                transition={{ type: 'spring', damping: 25, stiffness: 400 }}
                className="fixed z-[101] glass-dark rounded-xl overflow-hidden shadow-2xl min-w-[160px] border border-white/10"
                style={{
                  top: menuPosition.top,
                  right: menuPosition.right,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleEdit}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors text-left"
                >
                  <Edit2 size={16} />
                  <span className="text-sm">Edit</span>
                </button>
                <button
                  onClick={handleTogglePublic}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors text-left"
                >
                  {model.isPublic ? <EyeOff size={16} /> : <Eye size={16} />}
                  <span className="text-sm">
                    Make {model.isPublic ? 'Private' : 'Public'}
                  </span>
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-red-500/20 text-red-400 transition-colors text-left"
                >
                  <Trash2 size={16} />
                  <span className="text-sm">Delete</span>
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
